import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { monthsLocalized } from '../../constants/months.constant';
import {
  weekdaysLocalized,
  weekdaysShortLocalized,
} from '../../constants/weekdays.constant';
import { FriendlyDatePipe } from '../../pipes/friendly-date/friendly-date.pipe';
import { DateTimeService } from 'i2g-ng-utils';

const pikaday = require('pikaday');

@Directive({
  selector: '[datePicker]',
})
export class DatePickerDirective implements OnInit, OnChanges, OnDestroy {
  @Input('datePicker') containerElement: any;
  @Input() initialDate: string;
  @Input() minDate: string;
  @Input() maxDate: string;
  @Input() allowEmptyDate: boolean;

  @Output() onDateSelect: EventEmitter<string> = new EventEmitter<string>();
  @Output() onDatePickerOpen: EventEmitter<void> = new EventEmitter<void>();
  @Output() onDatePickerClose: EventEmitter<void> = new EventEmitter<void>();

  private _datePickerInstance: any = null;
  private _el: any = null;
  private _selectedDateValue: string;
  private _emptyDate: boolean;

  constructor(
    private _elementRef: ElementRef,
    private _friendlyDatePipe: FriendlyDatePipe,
    private _dateTimeService: DateTimeService,
    private _translateService: TranslateService
  ) {}

  @HostListener('keydown')
  onKeydown() {
    if (this._el) {
      this._el.value = this._selectedDateValue;
    }
  }

  @HostListener('keyup')
  onKeyup() {
    if (this._el) {
      this._el.value = this._selectedDateValue;
    }
  }

  @HostListener('paste')
  onPaste() {
    if (this._el) {
      this._el.value = this._selectedDateValue;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this._el) {
      this._el = this._elementRef.nativeElement;
    }

    this._emptyDate = this.allowEmptyDate && !this.initialDate;

    if (this._datePickerInstance) {
      if (changes.minDate && this.minDate) {
        this._datePickerInstance.setMinDate(this.buildDate(this.minDate));
      }

      if (changes.maxDate && this.maxDate) {
        this._datePickerInstance.setMaxDate(this.buildDate(this.maxDate));
      }

      if (changes.initialDate) {
        this._datePickerInstance.setDate(
          this._emptyDate ? null : this.buildDate(this.initialDate),
          true
        );

        this.setSelectedDate(this.initialDate);

        if (this._el) {
          this._el.value = this._selectedDateValue;
        }
      }
    }
  }

  ngOnInit() {
    this._el = this._elementRef.nativeElement;

    this.setSelectedDate(this.initialDate);
    this.configureDatePicker();
  }

  configureDatePicker(
    initialDate: string = this.initialDate,
    minDate: string = this.minDate,
    maxDate: string = this.maxDate,
    containerElement = this.containerElement
  ) {
    if (containerElement && !this._datePickerInstance) {
      this._datePickerInstance = new pikaday({
        field: this._el,
        container: containerElement,
        defaultDate: this._emptyDate ? null : this.buildDate(initialDate),
        setDefaultDate: !this._emptyDate,
        firstDay: this._dateTimeService.getFirstDayOfWeek(),
        onSelect: (localDate: Date) => {
          const momentInTime = this._dateTimeService.getDateString(
            this._dateTimeService.getDate(localDate)
          );

          this.setSelectedDate(momentInTime);

          if (this._el) {
            this._el.value = this._selectedDateValue;
          }

          this.onDateSelect.emit(momentInTime);
        },
        showDaysInNextAndPreviousMonths: true,
        minDate: minDate ? this.buildDate(minDate) : null,
        maxDate: maxDate ? this.buildDate(maxDate) : null,
        i18n: {
          months: monthsLocalized(this._translateService)
            .map((month) => month.key)
            .toArray(),
          weekdays: weekdaysLocalized(this._translateService)
            .map((weekday) => weekday.key)
            .toArray(),
          weekdaysShort: weekdaysShortLocalized(this._translateService)
            .map((weekdayShort) => weekdayShort.key)
            .toArray(),
        },
        onOpen: () => this.onDatePickerOpen.emit(),
        onClose: () => this.onDatePickerClose.emit(),
      });
    }
  }

  buildDate(date: string): Date {
    return this._dateTimeService.getDate(
      date || this._dateTimeService.getNow()
    );
  }

  setSelectedDate(selectedDate: string) {
    this._selectedDateValue =
      this._emptyDate && !selectedDate
        ? null
        : this._friendlyDatePipe.transform(
            selectedDate || this._dateTimeService.getNow()
          );
  }

  ngOnDestroy() {
    if (this._datePickerInstance) {
      this._datePickerInstance.destroy();
      this._datePickerInstance = null;
    }
  }
}
